import React, { useContext, useMemo, useCallback } from "react";
import useStoreWalletFund from "./useStoreWalletFund";
import primaryComponents from "../../../primaryComponents";
import { Validation } from "./Validation";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import styles from "./styles.module.scss";
import { formatCurrency } from "../../../../helpers";
import globalStyles from "../../../../assets/styles/base/globalPage.module.scss";
import LoaderHelper from "../../LoaderHelper/LoaderHelper";
import { useSelector } from "react-redux";
import { ToastContext } from "../../../../hooks/context";

const SplitBalanceComponent = React.memo(
  ({ fundDetails, splitBalance, onSplitChange, register }) => {
    return (
      <div
        className={`${styles["splitSection"]} ${globalStyles.cardWithBackground}`}
      >
        <h5 className="text-center mb-3">
          <span className="gray--text small-text-size mr-1">
            Split Balance:{" "}
          </span>
          <span
            className={`${splitBalance === 0 ? "success--text" : "error--text"}`}
          >
            {formatCurrency({ value: splitBalance })}
          </span>
        </h5>

        <div className={styles.formSection}>
          {/* Transfer */}
          <div className={styles.formField}>
            <primaryComponents.InputField
              type="number"
              name="split_transfer"
              label={"Transfer"}
              isRequired={true}
              placeholder="₦ Enter Amount"
              value={fundDetails.transfer_amount}
              onChange={({ target: { value } }) =>
                onSplitChange("transfer_amount", value)
              }
              classNames="white"
              register={register}
            />
          </div>

          {/* Card Row */}
          <div className={styles.formField}>
            <primaryComponents.InputField
              type="number"
              name="split_card"
              label={"Card"}
              isRequired={true}
              placeholder="₦ Enter Amount"
              value={fundDetails.card_amount}
              onChange={({ target: { value } }) =>
                onSplitChange("card_amount", value)
              }
              classNames="white"
              register={register}
            />
          </div>

          {/* Cash Row */}
          <div className={styles.formField}>
            <primaryComponents.InputField
              type="number"
              name="split_cash"
              label={"Cash"}
              isRequired={true}
              placeholder="₦ Enter Amount"
              value={fundDetails.cash_amount}
              onChange={({ target: { value } }) =>
                onSplitChange("cash_amount", value)
              }
              classNames="white"
              register={register}
            />
          </div>
        </div>
      </div>
    );
  },
);
SplitBalanceComponent.displayName = "SplitBalanceComponent";

const StoreWalletFund = ({ setIsFundWallet, customerDetails = {} }) => {
  const {
    fundDetails,
    storeList,
    isFundingWallet,
    setFundDetails,
    handleFundWallet,
    storeWallets,
    bankAccount,
    setSelectedBank,
    isLoadingBankAccountsList,
  } = useStoreWalletFund({
    customerDetails,
    setIsFundWallet,
  });
  const { allow_ledger_account } = useSelector(
    (state) => state.profileDetailsReducer.businessDetails,
  );

  const triggerToast = useContext(ToastContext);
  const {
    handleSubmit,
    formState: { errors },
    control,
    trigger,
    register,
  } = Validation({ fundDetails });

  // Use memo to avoid recalculation unless dependencies change
  const totalSplit = useMemo(() => {
    return (
      Number.parseFloat(fundDetails.transfer_amount || 0) +
      Number.parseFloat(fundDetails.card_amount || 0) +
      Number.parseFloat(fundDetails.cash_amount || 0)
    );
  }, [
    fundDetails.transfer_amount,
    fundDetails.card_amount,
    fundDetails.cash_amount,
  ]);

  const splitBalance = useMemo(() => {
    return Number.parseFloat(fundDetails.amount || 0) - totalSplit;
  }, [fundDetails.amount, totalSplit]);

  // Handle split changes efficiently
  const handleSplitChange = useCallback(
    (field, value) => {
      setFundDetails((prev) => ({ ...prev, [field]: value }));
    },
    [setFundDetails],
  );

  // Wrap the original handler to validate before submission
  const handleFundWalletWithSplit = () => {
    if (fundDetails?.payment_method === "split" && splitBalance !== 0)
      return triggerToast(
        "Please ensure your split balance is 0.00",
        "warning",
      );

    handleFundWallet();
  };

  // Reusable handler for updating fundDetails
  const handleChange = useCallback(
    (key) => (e) => {
      const { value } = e.target;
      setFundDetails((prev) => ({ ...prev, [key]: value }));
    },
    [setFundDetails],
  );

  return (
    <div className={globalStyles.pageColumnGaps} style={{ minHeight: "100%" }}>
      <div>
        <primaryComponents.Button
          classNames={"btn btn--outline smallBtn"}
          onClick={() => setIsFundWallet(null)}
        >
          <FontAwesomeIcon icon="angle-left" style={{ fontSize: "15px" }} />
        </primaryComponents.Button>
      </div>

      <div>
        <h4 className="mb-0">Fund Wallet</h4>
        <p>
          Fund customer&apos;s wallet instantly for quick and convenient
          transactions
        </p>
      </div>

      <form
        className={globalStyles.pageColumnGaps}
        style={{ gap: "10px" }}
        onSubmit={handleSubmit(handleFundWalletWithSplit)}
        id="fundWalletForm"
      >
        <primaryComponents.SelectFieldV2
          control={control}
          idKey={"id"}
          nameKey={"name"}
          name="walletId"
          isRequired={true}
          value={"Select wallet"}
          classNames="wallet-select"
          label={"Select wallet"}
          options={storeWallets?.data
            ?.filter((data) => data?.tag)
            ?.map((data) => ({
              id: data?.id,
              name: (
                <div
                  className="d-flex align-items-center justify-content-between"
                  style={{ gap: "30px" }}
                >
                  <div>{data?.tag}</div>
                  <div
                    className={`${data?.is_subwallet ? "gray--squared" : "gray--primary"}`}
                  >
                    {data?.is_subwallet ? "Sub-wallet" : "Primary Wallet"}
                  </div>
                </div>
              ),
            }))}
          onChange={(value) => {
            setFundDetails((prev) => ({ ...prev, wallet_id: value }));
          }}
          errorMessage={errors.walletId}
        />

        <primaryComponents.SelectFieldV2
          control={control}
          name="store"
          label={"Which store is the customer making the payment to?"}
          options={storeList}
          idKey="id"
          nameKey="name"
          isRequired={true}
          onChange={(value) => {
            setFundDetails((prev) => ({ ...prev, store_id: value }));
          }}
          value={"Select store"}
          classNames="store-select"
          errorMessage={errors.store}
        />
        <primaryComponents.InputFieldV2
          type="number"
          name="amount"
          control={control}
          label={"Credit Amount"}
          isRequired={true}
          placeholder="Enter total credit amount"
          value={fundDetails?.amount}
          onChange={handleChange("amount")}
          classNames="white"
          errorMessage={errors.amount && { message: errors.amount.message }}
          onKeyUp={() => errors.amount !== undefined && trigger("amount")}
          register={register}
        />

        <primaryComponents.SelectFieldV2
          control={control}
          name="paymentMethod"
          options={["transfer", "card", "cash", "split"]}
          label={"Payment Method"}
          isRequired={true}
          value={fundDetails?.payment_method || "Select Payment Type"}
          onChange={(value) => {
            setFundDetails((prev) => ({ ...prev, payment_method: value }));
          }}
          idKey="value"
          register={register}
          errorMessage={errors.paymentMethod}
        />

        {allow_ledger_account && fundDetails?.payment_method !== "cash" ? (
          <LoaderHelper isLoading={isLoadingBankAccountsList} className="mt-1">
            <primaryComponents.SelectField
              options={bankAccount?.data?.map((bank) => ({
                id: bank?.id,
                name: bank?.account_name,
              }))}
              idKey="id"
              nameKey="name"
              label={"Select Bank Account"}
              value={
                bankAccount ? bankAccount?.data[0]?.account_name : "Select Bank"
              }
              onChange={(value) => {
                setSelectedBank(value);
              }}
              floatError={false}
              classNames="bank-name"
              isSearchable={false}
            />
          </LoaderHelper>
        ) : null}
        {fundDetails?.payment_method === "split" && (
          <SplitBalanceComponent
            fundDetails={fundDetails}
            splitBalance={splitBalance}
            onSplitChange={handleSplitChange}
            register={register}
          />
        )}

        <primaryComponents.TextAreaV2
          control={control}
          type="text"
          label={"Description"}
          name="note"
          placeholder="Add description for this fund wallet"
          value={fundDetails?.description}
          onChange={handleChange("description")}
          errorMessage={errors.note}
          onKeyUp={() => errors.note !== undefined && trigger("note")}
          register={register}
        />
      </form>

      <primaryComponents.Button
        classNames="btn btn--primary cover mt-auto"
        loadingText="Funding..."
        type={"submit"}
        form={"fundWalletForm"}
        isLoading={isFundingWallet}
        isDisabled={isFundingWallet}
      >
        Proceed
      </primaryComponents.Button>
    </div>
  );
};

export default React.memo(StoreWalletFund);
